import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import { setAdminSession, shouldSkipAutoLogin, clearAuthBounce, isAdminAuthenticated } from '../../utils/auth';
import TwoFactorOtpStep from '../../components/TwoFactorOtpStep';
import './admin.css';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [email, setEmail] = useState('');
  const [awaiting2fa, setAwaiting2fa] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('adminRememberUsername');
    if (saved) {
      setUsername(saved);
      setRemember(true);
    }
  }, []);

  useEffect(() => {
    if (!shouldSkipAutoLogin() && isAdminAuthenticated()) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [navigate]);

  const completeLogin = (data) => {
    clearAuthBounce();
    setAdminSession(data.admin_id);
    localStorage.setItem('adminUsername', data.username || username);
    if (data.image) localStorage.setItem('adminImage', data.image);
    else localStorage.removeItem('adminImage');
    if (remember) localStorage.setItem('adminRememberUsername', username);
    else localStorage.removeItem('adminRememberUsername');
    navigate('/admin/dashboard', { replace: true });
  };

  const resend2faOtp = async () => {
    try {
      await api.post('admin/resend-2fa-otp/');
      toast.success('OTP resent to your email', {
        position: 'bottom-right',
        autoClose: 3000,
      });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to resend OTP', {
        position: 'bottom-right',
        autoClose: 3000,
      });
      throw err;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (awaiting2fa) {
        const res = await api.post('admin/verify-2fa/', {
          username: username.trim(),
          otp: otp.trim(),
        });
        setAwaiting2fa(false);
        toast.success(res.data.msg || 'Login successful!', {
          position: 'bottom-right',
          autoClose: 2000,
        });
        completeLogin(res.data);
        return;
      }

      const res = await api.post('admin/login/', { username, password });

      if (res.data?.requires_2fa) {
        setAwaiting2fa(true);
        setEmail(res.data.email || '');
        setOtp('');
        toast.success(res.data.msg || 'OTP sent to your email', {
          position: 'bottom-right',
          autoClose: 3000,
        });
        return;
      }

      completeLogin(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Incorrect username or password. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`admin-login position-relative${awaiting2fa ? ' is-2fa' : ''}`}>
      <Link
        to="/"
        className="btn btn-link btn-sm text-muted text-decoration-none position-absolute top-0 start-0 m-3"
      >
        ← Back to site
      </Link>
      <div className={`admin-login-card${awaiting2fa ? ' is-2fa' : ''}`}>
        <div className="admin-login-logo">
          <div className="admin-login-logo-mark">
            <i className={`bi ${awaiting2fa ? 'bi-shield-lock-fill' : 'bi-heart-pulse-fill'}`} />
          </div>
          <div className="admin-login-logo-name">FITNOVA</div>
          <div className="admin-login-logo-tag">
            {awaiting2fa ? 'TWO-FACTOR AUTHENTICATION' : 'HEALTH · FITNESS · WELLNESS'}
          </div>
        </div>

        <p className="admin-login-lead">
          {awaiting2fa
            ? 'Enter the 6-digit code sent to your email'
            : 'Sign in to continue to your account'}
        </p>

        {error && (
          <div className="admin-login-alert" role="alert">
            <i className="bi bi-exclamation-circle-fill" />
            <span>{error}</span>
            <button type="button" onClick={() => setError('')} aria-label="Dismiss">
              <i className="bi bi-x-lg" />
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="admin-login-form">
          {awaiting2fa ? (
            <TwoFactorOtpStep
              email={email}
              otp={otp}
              onOtpChange={setOtp}
              onResend={resend2faOtp}
              onBack={() => {
                setAwaiting2fa(false);
                setOtp('');
                setPassword('');
                setError('');
              }}
              loading={loading}
            />
          ) : (
            <>
              <label className="admin-login-label" htmlFor="admin-username">
                Username
              </label>
              <div className="admin-login-field">
                <i className="bi bi-person" />
                <input
                  id="admin-username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  autoComplete="username"
                  required
                />
              </div>

              <label className="admin-login-label" htmlFor="admin-password">
                Password
              </label>
              <div className="admin-login-field">
                <i className="bi bi-lock" />
                <input
                  id="admin-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="admin-login-eye"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
                </button>
              </div>

              <div className="admin-login-row">
                <label className="admin-login-check">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                  />
                  Remember me
                </label>
                <Link to="/admin/forgot-password" className="admin-login-forgot">
                  Forgot Password?
                </Link>
              </div>

              <button type="submit" className="admin-login-submit" disabled={loading}>
                <i className="bi bi-lock-fill" />
                {loading ? 'Signing in…' : 'Login to Dashboard'}
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
